// The App Store and Google Play badges of the landing page: the stores' own
// artwork (Apple and Google ask for their unmodified badges), linked to the
// office's store links (settings.AppleStoreLink / PlayStoreLink); a store without
// a link is left out. The badge words are the link's name, and screen readers
// hear that it opens a new tab. ring: the keyboard focus ring, chosen for 3:1 on
// the background the badges sit on.
import React from 'react';
import { Box, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import appStoreBadge from '../../assets/img/appstore.svg';
import playStoreBadge from '../../assets/img/playstore.svg';
import landingWord from './landingWords';

// Hidden from the eye, read by screen readers. Sizes as strings: in sx, 1 means 100 %.
export const srOnly = {
  position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px',
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

const linkSx = (ring) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  // 46 high on a phone, so the two badges fit side by side on a 360-wide screen
  // (44 is the touch minimum).
  height: { xs: 46, sm: 56 },
  borderRadius: '12px',
  transition: 'transform 160ms ease-out',
  '&:hover': { transform: 'translateY(-2px)' },
  '&:active': { transform: 'scale(0.97)' },
  '&:focus-visible': { outline: `3px solid ${ring}`, outlineOffset: 3 },
});

export default function StoreButtons({ ring = '#FFB08F', sx }) {
  const settings = useSelector((state) => state.settingsdata.settings);
  const { t, i18n } = useTranslation();
  // As on the page: /languages, then the bundled am / om / ti words, then English.
  const L = (key, en) => t(key, { defaultValue: landingWord(i18n.language, key, en) });
  const apple = settings && settings.AppleStoreLink;
  const play = settings && settings.PlayStoreLink;
  if (!apple && !play) return null;
  const newTab = <Box component="span" sx={srOnly}>{' ' + L('lp_new_tab', '(opens in a new tab)')}</Box>;
  return (
    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={sx}>
      {apple ? (
        <Box component="a" href={apple} target="_blank" rel="noopener noreferrer" sx={linkSx(ring)}>
          {/* 135 x 40 artwork, the badge edge to edge */}
          <Box component="img" src={appStoreBadge} alt={L('lp_store_ios_alt', 'Download on the App Store')} sx={{ height: { xs: 46, sm: 56 }, width: 'auto', display: 'block', pointerEvents: 'none' }} />
          {newTab}
        </Box>
      ) : null}
      {play ? (
        <Box component="a" href={play} target="_blank" rel="noopener noreferrer" sx={linkSx(ring)}>
          {/* 150 x 50 artwork whose badge is Apple's 135 x 40 at x 10, y 10: clear space
              above and on the left (10), on the right (5), none below. Drawn 1.4 times
              (1.15 on a phone) with exactly that trimmed, the badge is Apple's size and
              level with it (owner, 5 Nov 2026: the two were not aligned; the old even
              trim left it 6-8 px low). The image takes no clicks: the trimmed corner
              must not catch a tap meant for the App Store. */}
          <Box
            component="img"
            src={playStoreBadge}
            alt={L('lp_store_android_alt', 'Get it on Google Play')}
            sx={{
              height: { xs: '57.5px', sm: '70px' },
              width: 'auto',
              display: 'block',
              pointerEvents: 'none',
              mt: { xs: '-11.5px', sm: '-14px' },
              mb: 0,
              ml: { xs: '-11.5px', sm: '-14px' },
              mr: { xs: '-5.75px', sm: '-7px' },
            }}
          />
          {newTab}
        </Box>
      ) : null}
    </Stack>
  );
}
